import { api } from '@alpha/api-client';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { PageHeader } from '../../components/PageHeader';
import { BinaryTreeView } from '../../components/tree/BinaryTreeView';
import { formatNumber } from '../../lib/format';
import { Button, Card, EmptyState, FormGroup, Input, Spinner, StatCard, StatCards } from '../../ui';

/** Same render-depth cap as the user panel — keeps each fetch small (at most
 *  2^4 - 1 = 15 nodes) regardless of how deep the real tree goes; drilling
 *  into a truncated leg re-roots the query rather than loading everything. */
const RENDER_DEPTH = 4;

interface TrailEntry {
  userId: string;
  code: string;
}

/**
 * Admin-only binary tree lookup: GET /admin/members/{id}/binary-tree, gated by
 * the `admin` route middleware (routes/api.php) — distinct from the
 * user-panel's own-tree-only /team/binary-tree. Admins can type ANY member's
 * numeric ID here, matching the ID used throughout the rest of the admin
 * panel (see MemberSearch.tsx / MemberDetail.tsx's `/members/view/:id`).
 */
export function BinaryTree() {
  const [inputId, setInputId] = useState('');
  const [trail, setTrail] = useState<TrailEntry[]>([]);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const activeId = trail.length > 0 ? trail[trail.length - 1].userId : undefined;

  const { data: root, isLoading, isFetching, error } = useQuery({
    queryKey: ['admin-binary-tree', activeId],
    queryFn: () => api.team.adminTree(activeId!, RENDER_DEPTH).then((r) => r.data),
    enabled: !!activeId,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const id = inputId.trim();
    if (!id) return;
    setTrail([{ userId: id, code: id }]);
  };

  const handleDrill = (node: { user_id: string; user_code: string }) => {
    setPendingId(node.user_id);
    setTrail((t) => [...t, { userId: node.user_id, code: node.user_code }]);
  };

  if (!isFetching && pendingId) setPendingId(null);

  return (
    <>
      <PageHeader
        title="Binary Tree"
        description="Look up any member's binary placement tree by their ID number. Hover (or tap) a member for details."
      />

      <Card>
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '0 1 220px', minWidth: 180 }}>
            <FormGroup label="Member ID">
              <Input
                placeholder="e.g. 1042"
                value={inputId}
                onChange={(e) => setInputId(e.target.value)}
              />
            </FormGroup>
          </div>
          <Button type="submit" variant="gold">
            View Tree
          </Button>
        </form>
      </Card>

      {trail.length > 0 && (
        <div className="flex gap-1 mb-2 mt-2" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
          {trail.map((t, i) => (
            <Button
              key={`${t.userId}-${i}`}
              variant={i === trail.length - 1 ? 'gold' : 'ghost'}
              size="sm"
              onClick={() => setTrail((cur) => cur.slice(0, i + 1))}
            >
              ID {t.code}
            </Button>
          ))}
        </div>
      )}

      {activeId && root && (
        <StatCards>
          <StatCard label="Left Leg" value={formatNumber(root.left_count)} />
          <StatCard label="Right Leg" value={formatNumber(root.right_count)} />
          <StatCard label="Matched" value={formatNumber(Math.min(root.left_count, root.right_count))} />
        </StatCards>
      )}

      <Card>
        {!activeId ? (
          <EmptyState title="Enter a member ID above to view their binary tree" />
        ) : isLoading ? (
          <Spinner />
        ) : error ? (
          <EmptyState title="That member ID could not be found" />
        ) : root ? (
          <BinaryTreeView root={root} onDrill={handleDrill} pendingDrillId={pendingId} />
        ) : (
          <EmptyState title="No placement yet" />
        )}
      </Card>
    </>
  );
}
